// 帖子相关的 Action 层，负责业务逻辑和异步操作
import { action, makeObservable, runInAction } from 'mobx';
import { ActivityStore } from './store';
import { readactivityList, readactivityFavoriteList, readActivityDetail, readPostList, votesOptions, postsCreate, reports, createFollow, followDelete, createUserslikes, threadFavorite } from '@server';


export class ActivityAction extends ActivityStore {

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    /**
     * 获取活动列表（异步）
     * 统一管理 loading、错误、分页、hasMore 等状态
     * 页面和 list 组件可直接响应 listStore 的状态
     * @param {object} params 查询参数
     *   - params.page[number]: 页码
     *   - params.page[size]: 每页数量
     *   - params.filter: 过滤条件（可选）
     * @returns {Promise<void>}
     */


    @action
    async getActivityList(params) {
        runInAction(() => {
            this.listStore.setAttribute({ namespace: 'activityList', key: 'loading', value: true });
        });
        const res = await readactivityList(params);
        runInAction(() => {
            console.log('Activity list response:', res, 'Params:', params);
            if (res.code === 0) {
                // 适配新格式：数据在 res.data.data，分页在 res.data.page
                const activityData = res.data && res.data.data ? res.data.data : res.data;
                const pageInfo = res.data && res.data.page ? res.data.page : {};
                
                this.listStore.setList({
                    namespace: 'activityList',
                    data: { data: activityData },
                    path: 'data',
                    page: params.page || params['page[number]'] || pageInfo.number || 1,
                    search: { filter: params.filter || {} },
                    hasMore: pageInfo.hasMore !== undefined ? pageInfo.hasMore : false,
                });
                this.listStore.setAttribute({ namespace: 'activityList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'activityList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'activityList', key: 'loading', value: false });
        });
        // console.log('这是活动列表呀', res);
        return res
    }

    /**
     * 获取用户收藏活动列表（异步）
     * 统一管理 loading、错误、分页、hasMore 等状态
     * 页面和 list 组件可直接响应 listStore 的状态
     * @param {object} params 查询参数
     *   - params.page[number]: 页码
     *   - params.page[size]: 每页数量
     *   - params.filter: 过滤条件（可选）
     * @returns {Promise<void>}
     */
    @action
    async getActivityFavoriteList(params) {
        runInAction(() => {
            this.listStore.setAttribute({ namespace: 'activityFavoriteList', key: 'loading', value: true });
        });
        const res = await readactivityFavoriteList(params);
        runInAction(() => {
            console.log('Activity favorite list response:', res, 'Params:', params);
            if (res.code === 0) {
                // 适配新格式：数据在 res.data.data，分页在 res.data.page
                const activityData = res.data && res.data.data ? res.data.data : res.data;
                const pageInfo = res.data && res.data.page ? res.data.page : {};
                
                this.listStore.setList({
                    namespace: 'activityFavoriteList',
                    data: { data: activityData },
                    path: 'data',
                    page: params.page || params['page[number]'] || pageInfo.number || 1,
                    search: { filter: params.filter || {} },
                    hasMore: pageInfo.hasMore !== undefined ? pageInfo.hasMore : false,
                });
                this.listStore.setAttribute({ namespace: 'activityFavoriteList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'activityFavoriteList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'activityFavoriteList', key: 'loading', value: false });
        });
        // console.log('这是活动列表呀', res);
        return res
    }

    /**
     * 获取活动详情
     * @param {number} id 活动id
     * @returns 活动详细信息
     */
    @action
    async getActivityDetail(id, params = {}) {
        const ret = await readActivityDetail(id, params);
        
        if (ret.code === 0) this.setActivityData(ret.data);
        return ret;
    }

    /**
     * 设置 activityData
     * @param {object} data 活动详情数据
     */
    @action
    setActivityData(data) {
        this.activityData = { ...this.activityData, ...data };
        this.activityData.id = data.id;
    }

    /**
     * 获取活动评论列表
     * @param {object} params 查询参数，需包含 filter[type]=2
     */
    @action
    async getActivityPostList(params) {
        runInAction(() => {
            this.listStore.setAttribute({ namespace: 'activityPostList', key: 'loading', value: true });
        });
        
        // 2. 确保 filter[type] = 2（活动评论）
        const requestParams = {
            ...params,
            'filter[type]': 2
        };
        
        // 3. 发起异步请求
        const res = await readPostList(requestParams);
        const code = typeof res.code !== 'undefined' ? res.code : res.Code;
        const rawData = res.data || res.Data || {};
        const list = rawData.data || rawData.pageData || [];
        const pageInfo = rawData.page || rawData.Page || {};
        const currentPage = pageInfo.number || params['page[number]'] || params.page || 1;
        const totalPage = pageInfo.totalPage;
        const hasMore = typeof pageInfo.hasMore === 'boolean'
            ? pageInfo.hasMore
            : (typeof totalPage === 'number' ? currentPage < totalPage : false);

        runInAction(() => {
            if (code === 0) {
                this.listStore.setList({ namespace: 'activityPostList', data: { data: list }, page: currentPage, path: 'data', hasMore });
                this.listStore.setAttribute({ namespace: 'activityPostList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'activityPostList', key: 'requestError', value: { isError: true, errorText: res.message || res.Message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'activityPostList', key: 'loading', value: false });
        });
    }

    /**
     * 投票
     * @param {number} vote 选项id
     * @param {object} params 选项id合集
     * @returns 投票结果
     */
    @action
    async createVotesOptions(vote, params) {
        const ret = await votesOptions(vote, params);
        if(ret.code == 0) {
            
        }
        
        return ret;
    }

    // 打赏活动
    async postsCreate(params) {
        const ret = await postsCreate(params);
        if(ret.code == 0) {
           
        }
        
        return ret;
    }

    // 举报活动
    @action
    async createReports(params) {
        const ret = await reports(params);
        if(ret.code == 0) {
            // 更新列表store
            // this.updateListStore();
        }
        
        return ret;
    }

    // 关注用户
    @action
    async createFollowUser(params) {
        const res = await createFollow(params);
        if(res.code == 0) {
            // 更新 activityData 中的关注状态
            if (this.activityData && this.activityData.user && this.activityData.user.code === params.toUserCode) {
                this.activityData.user.isFollow = true;
            }
        }
        return res
    }

    // 取消关注用户或者移除粉丝
    @action
    async followDelete(params) {
        const res = await followDelete(params);
        if(res.code == 0) {
            // 更新 activityData 中的关注状态
            if (this.activityData && this.activityData.user && this.activityData.user.code === params.code) {
                this.activityData.user.isFollow = false;
            }
        }
        return res
    }

    // 立即更新点赞状态（用于UI反馈）
    @action
    updateLikeStatus(isLiked) {
        if (this.activityData) {
            this.activityData.isLiked = isLiked;
        }
    }

    // 立即更新收藏状态（用于UI反馈）
    @action
    updateFavoriteStatus(isFavorite) {
        if (this.activityData) {
            this.activityData.isFavorite = isFavorite;
        }
    }

    // 活动点赞
    @action
    async createUserslikes(params) {
        const ret = await createUserslikes(params);
        if(ret.code == 0) {
            // 在 runInAction 中更新状态，确保符合 strict 模式要求
            runInAction(() => {
                // 接口成功后，更新服务器返回的数据
                if (params.type === 'activity') {
                    if (this.activityData && (this.activityData.id === params.id)) {
                        this.activityData = {
                            ...this.activityData,
                            isLiked: !!params.isLiked,
                            likeCount: typeof ret.data?.likeCount === 'number'
                                ? ret.data.likeCount
                                : (typeof this.activityData.likeCount === 'number'
                                    ? (params.isLiked ? this.activityData.likeCount + 1 : Math.max(0, this.activityData.likeCount - 1))
                                    : ret.data?.likeCount)
                        };
                    }
                }
                // 更新评论列表
                else if (params.type === 'post') {
                    const updates = {
                        isLiked: params.isLiked,
                    };
                    if (typeof ret.data?.likeCount === 'number') {
                        updates.likeCount = ret.data.likeCount;
                    }
                    this.listStore.updateListItem({
                        namespace: 'activityPostList',
                        id: params.id,
                        updates
                    });
                }
            });
        }
        return ret;
    }

    // 活动收藏
    @action
    async activityFavorite(params) {
        const ret = await threadFavorite(params);
        if(ret.code == 0) {
            // 在 runInAction 中更新状态，确保符合 strict 模式要求
            runInAction(() => {
                const currentActivityId = this.activityData?.id;
                
                if (this.activityData && currentActivityId === params.id) {
                    // 如果API返回了favoriteCount，使用API的数据；否则手动计算
                    let newFavoriteCount = this.activityData.favoriteCount || 0;
                    
                    if (typeof ret.data?.favoriteCount === 'number') {
                        newFavoriteCount = ret.data.favoriteCount;
                    } else {
                        // API未返回favoriteCount时，手动计算
                        if (params.isFavorite) {
                            // 收藏操作：数量 +1
                            newFavoriteCount = (this.activityData.favoriteCount || 0) + 1;
                        } else {
                            // 取消收藏操作：数量 -1
                            newFavoriteCount = Math.max(0, (this.activityData.favoriteCount || 0) - 1);
                        }
                    }
                    
                    this.activityData = {
                        ...this.activityData,
                        isFavorite: params.isFavorite,
                        favoriteCount: newFavoriteCount
                    };
                }
            });
        }
        return ret;
    }

    // 获取ID辅助方法
    getId(data) {
        if (!data) return undefined;
        // 兼容 MobX Proxy、普通对象
        if (typeof data.id !== 'undefined') return data.id;
        if (typeof data.get === 'function') return data.get('id');
        // 兜底遍历属性
        for (const key in data) {
            if (key === 'id') return data[key];
        }
        return undefined;
    }

    /**
     * 直接设置 activityList（通常不推荐，建议用 listStore 统一管理）
     * @param {array} activityList 帖子列表
     */
    @action
    setactivityList(activityList) {
        this.activityList = activityList;
    }

}


// 单例导出，方便全局调用
const activityAction = new ActivityAction();
export default activityAction;